import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";

const ThemeContext = createContext();

export function useThemeMode() {
  return useContext(ThemeContext);
}

function getInitialTheme() {
  const saved = localStorage.getItem("ikaro_theme_preference");
  if (saved === "dark" || saved === "light") return saved;
  if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }
  return "light";
}

export function ThemeModeProvider({ children }) {
  const [themeMode, setThemeMode] = useState(getInitialTheme);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", themeMode);
    localStorage.setItem("ikaro_theme_preference", themeMode);
  }, [themeMode]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e) => {
      if (!localStorage.getItem("ikaro_theme_preference")) {
        setThemeMode(e.matches ? "dark" : "light");
      }
    };
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeMode((prev) => (prev === "light" ? "dark" : "light"));
  }, []);

  const isDark = themeMode === "dark";

  /* Memoizamos o `value` do contexto: sem isto, qualquer re-render do
     `ThemeModeProvider` (ou de seus ancestrais) cria um objeto novo, o
     que invalida `React.memo` em todos os consumidores (ex.: cada
     `SpeakerBadge` da lista de transcrições) e gera renderizações
     desnecessárias em cascata. */
  const value = useMemo(
    () => ({ themeMode, isDark, toggleTheme, setThemeMode }),
    [themeMode, isDark, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
